import { DEFAULT_LOCALE, type Locale } from "./locales.ts";
import { bcp47 } from "./resolve.ts";

/**
 * Numbers, money and dates, in the reader's language.
 *
 * Everything goes through `Intl`. CLAUDE.md bans raw `Date` arithmetic for user-facing
 * times; none happens here — an instant arrives already decided and is rendered into an
 * **explicit** IANA zone, which is the platform's own zone-correct renderer.
 *
 * No catalog is imported, so this is free to a bundle that only wants to format a price.
 */

export function formatNumber(value: number, locale: Locale = DEFAULT_LOCALE): string {
  return new Intl.NumberFormat(bcp47(locale)).format(value);
}

/**
 * Money, from integer minor units and an explicit currency (CLAUDE.md).
 *
 * `Intl` puts the symbol where the locale wants it — `1 250 kr` in Swedish, `kr 1 250` in
 * Norwegian, `SEK 1,250` in English — which is the whole reason this is not a template
 * string with a suffix. Whole units by default: prices in this trade are whole kronor and
 * "4 500,00 kr" is noise.
 */
export function formatMoney(
  minor: number,
  currency: string,
  locale: Locale = DEFAULT_LOCALE,
): string {
  return new Intl.NumberFormat(bcp47(locale), {
    style: "currency",
    currency,
    minimumFractionDigits: minor % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(minor / 100);
}

/** `2026-08-22` in Swedish and Norwegian, `22/08/2026` in English. */
export function formatDate(
  instant: Date,
  timezone: string,
  locale: Locale = DEFAULT_LOCALE,
): string {
  return new Intl.DateTimeFormat(bcp47(locale), {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: timezone,
  }).format(instant);
}

/** `09:00`. 24-hour in every locale this product ships, including `en-GB`. */
export function formatTime(
  instant: Date,
  timezone: string,
  locale: Locale = DEFAULT_LOCALE,
): string {
  return new Intl.DateTimeFormat(bcp47(locale), {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: timezone,
  }).format(instant);
}
